import React, { useState, useEffect } from "react";
import logo from "../assets/logo.png";
import {
    Calendar,
    MapPin,
    Phone,
    Mail,
    Clock,
    CheckCircle,
    ArrowRight,
    Menu,
    X,
    Users,
    Award,
    Heart,
    Target,
    Zap,
    Shield,
    Star,
    ChevronLeft,
    ChevronRight,
} from "lucide-react";

export default function ActiveMotionLanding() {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [activeTestimonial, setActiveTestimonial] = useState(0);
    const [activeService, setActiveService] = useState(null);
    const whatsappNumber = "918758009100"; // Replace with your WhatsApp number (with country code, no + or spaces)
    const contactNumber = "+91 87 5800 9100";
    const emailAddress = "info@activemotion.co.in";

    const clinicAddress = {
        line1: (
            <>
                103, 1
                <sup className="text-[0.6em] align-super font-medium text-gray-400 ml-[1px]">
                    st
                </sup>{" "}
                Floor, Doctor House
            </>
        ),
        line2: "Radhe Krishna Residency",
        landmark: "Near Dindoli Fire Station",
        area: "Dindoli",
        city: "Surat",
        zip: "394210",
    };

    const [formData, setFormData] = useState({
        name: "",
        phone: "",
        email: "",
        concern: "",
        message: "",
    });

    const handleInputChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };
    const handleWhatsAppSubmit = (e) => {
        e.preventDefault();

        // Simple, clean message format
        const whatsappMessage = `NEW APPOINTMENT REQUEST

Name: ${formData.name}
Phone: ${formData.phone}
Email: ${formData.email || "Not provided"}
Service Needed: ${formData.concern}

Additional Details:
${formData.message || "None"}

------
From: Active Motion Website`;

        const encodedMessage = encodeURIComponent(whatsappMessage);

        // IMPORTANT: Replace with your actual WhatsApp number
        // Format: country code + number (no + sign, no spaces)

        // Open WhatsApp
        window.open(
            `https://wa.me/${whatsappNumber}?text=${encodedMessage}`,
            "_blank",
        );

        // Optional: Show success message
        // alert('Opening WhatsApp... Please click Send in WhatsApp to complete your booking!');

        // // Clear form
        // setFormData({
        //     name: "",
        //     phone: "",
        //     email: "",
        //     concern: "",
        //     message: "",
        // });
    };

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 50);
        };
        document.title = "Active Motion Physiotherapy Clinic - Surat";

        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    // Comprehensive services from Instagram
    const services = [
        {
            title: "Women's Health Physiotherapy",
            description:
                "Specialized care for knee pain during pregnancy, post-pregnancy recovery, hormonal changes, and pelvic health",
            icon: "👶",
            color: "#FF6B9D",
            conditions: [
                "Pregnancy-related pain",
                "Post-natal recovery",
                "Pelvic floor dysfunction",
                "Hormonal joint issues",
            ],
        },
        {
            title: "Knee Pain Management",
            description:
                "Expert treatment for knee pain affecting women, arthritis prevention, and strengthening weak quadriceps & glutes",
            icon: "🦵",
            color: "#E8C468",
            conditions: [
                "Weak quadriceps",
                "Weak glutes",
                "Post-pregnancy knee pain",
                "Arthritis prevention",
            ],
        },
        {
            title: "Back Pain Relief",
            description:
                "Comprehensive treatment for lower back pain, sciatica, disc problems, and core strengthening",
            icon: "🔥",
            color: "#B8D77E",
            conditions: ["Tight back", "Weak core", "Sciatica", "Disc issues"],
        },
        {
            title: "Neck & Spine Care",
            description:
                "Advanced care for neck pain, cervical spondylosis, smartphone posture, and upper back weakness",
            icon: "💪",
            color: "#5A8A7A",
            conditions: [
                "Tight neck",
                "Weak upper back",
                "Tech neck",
                "Cervical issues",
            ],
        },
        {
            title: "Plantar Fasciitis & Heel Pain",
            description:
                "Specialized treatment for heel pain, morning foot pain, and high heel-related foot problems",
            icon: "👠",
            color: "#FF8C42",
            conditions: [
                "Heel tenderness",
                "Morning stiffness",
                "Foot inflammation",
                "High heel damage",
            ],
        },
        {
            title: "Posture Correction",
            description:
                "Comprehensive posture analysis and correction for long-term spinal health and pain prevention",
            icon: "🎯",
            color: "#4A90E2",
            conditions: [
                "Forward head posture",
                "Rounded shoulders",
                "Ergonomic issues",
                "Spinal alignment",
            ],
        },
        {
            title: "Sports Injury Rehabilitation",
            description:
                "Recovery and prevention programs for athletes and active individuals",
            icon: "⚡",
            color: "#9B59B6",
            conditions: [
                "Muscle strains",
                "Ligament injuries",
                "Performance optimization",
                "Injury prevention",
            ],
        },
        {
            title: "Arthritis Management",
            description:
                "Natural healing and pain management without heavy medication or long-term dependency",
            icon: "🌿",
            color: "#27AE60",
            conditions: [
                "Joint stiffness",
                "Mobility restoration",
                "Pain relief",
                "Natural healing",
            ],
        },
    ];

    // Doctor information
    const doctors = [
        {
            name: "Dr. Mosam Patel",
            designation: "Physiotherapist (PT)",
            image: "👩‍⚕️",
            color: "#4A90E2",
            quote: "Movement is not just medicine—it's prevention, healing, and empowerment. Every patient deserves a personalized path to lasting wellness.",
            expertise: [
                "Sports Rehabilitation",
                "Manual Therapy",
                "Biomechanics",
                "Posture Correction",
            ],
        },
        {
            name: "Dr. Aayushi Patel",
            designation: "Physiotherapist (PT)",
            image: "👩‍⚕️",
            color: "#50C878",
            quote: "Listen to your body. Treat the cause, not just the pain. True healing comes from addressing the root problem with science-backed care.",
            expertise: [
                "Women's Health",
                "Prenatal & Postnatal Care",
                "Knee Rehabilitation",
                "Chronic Pain Management",
            ],
        },
    ];

    const testimonials = [
        {
            name: "Priya Mehta",
            issue: "Post-pregnancy knee pain",
            rating: 5,
            text: "Dr. Aayushi understood my struggle as a new mom. After just 8 weeks, my knee pain is completely gone and I can carry my baby without worry!",
            result: "100% pain-free",
        },
        {
            name: "Rahul Shah",
            issue: "Chronic back pain",
            rating: 5,
            text: "Dr. Mosam's approach is different—she doesn't just treat symptoms. The combination of strengthening and posture correction changed my life.",
            result: "No more medication",
        },
        {
            name: "Anjali Desai",
            issue: "Plantar fasciitis",
            rating: 5,
            text: "I couldn't walk in the morning without severe heel pain. Now I'm back to my morning walks thanks to Active Motion's expert care!",
            result: "Back to active life",
        },
        {
            name: "Karan Patel",
            issue: "Neck pain from desk work",
            rating: 5,
            text: "The posture correction exercises and ergonomic advice have made my work-from-home life so much better. Highly recommend!",
            result: "Improved productivity",
        },
    ];

    const nextTestimonial = () => {
        setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    };

    const prevTestimonial = () => {
        setActiveTestimonial(
            (prev) => (prev - 1 + testimonials.length) % testimonials.length,
        );
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 font-sans overflow-x-hidden">
            <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800;900&family=Poppins:wght@300;400;500;600;700;800;900&display=swap');
        
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        
        body {
          font-family: 'Poppins', sans-serif;
          overflow-x: hidden;
        }
        
        .font-display {
          font-family: 'Outfit', sans-serif;
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(5deg); }
        }
        
        @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateX(-50px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(50px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.8);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        
        @keyframes shimmer {
          0% { background-position: -1000px 0; }
          100% { background-position: 1000px 0; }
        }
        
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        
        .animate-slide-in-left {
          animation: slideInLeft 0.8s ease-out forwards;
        }
        
        .animate-slide-in-right {
          animation: slideInRight 0.8s ease-out forwards;
        }
        
        .animate-fade-in-up {
          animation: fadeInUp 0.6s ease-out forwards;
        }
        
        .animate-scale-in {
          animation: scaleIn 0.5s ease-out forwards;
        }
        
        .animate-pulse {
          animation: pulse 2s ease-in-out infinite;
        }
        
        .animate-shimmer {
          animation: shimmer 3s ease-in-out infinite;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent);
          background-size: 1000px 100%;
        }
        
        .delay-100 { animation-delay: 0.1s; }
        .delay-200 { animation-delay: 0.2s; }
        .delay-300 { animation-delay: 0.3s; }
        .delay-400 { animation-delay: 0.4s; }
        .delay-500 { animation-delay: 0.5s; }
        .delay-600 { animation-delay: 0.6s; }
        .delay-700 { animation-delay: 0.7s; }
        .delay-800 { animation-delay: 0.8s; }
        
        .glass-effect {
          background: rgba(255, 255, 255, 0.85);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.3);
        }
        
        .service-card {
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .service-card:hover {
          transform: translateY(-10px) scale(1.02);
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
        }
        
        .gradient-text {
          background: linear-gradient(135deg, #4A90E2 0%, #50C878 50%, #FFD700 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        
        .mesh-gradient {
          background: 
            radial-gradient(at 0% 0%, rgba(74, 144, 226, 0.15) 0px, transparent 50%),
            radial-gradient(at 100% 0%, rgba(80, 200, 120, 0.15) 0px, transparent 50%),
            radial-gradient(at 100% 100%, rgba(255, 215, 0, 0.15) 0px, transparent 50%),
            radial-gradient(at 0% 100%, rgba(74, 144, 226, 0.15) 0px, transparent 50%);
        }
        
        .blob {
          border-radius: 30% 70% 70% 30% / 30% 30% 70% 70%;
          animation: float 8s ease-in-out infinite;
        }
      `}</style>

            {/* Navigation */}
            <nav
                className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "glass-effect shadow-lg" : "bg-transparent"}`}
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-20">
                        <div className="flex items-center space-x-3 animate-slide-in-left">
                            {/* add logo here */}
                            <div className="w-14 h-14 rounded-full flex items-center justify-center shadow-lg bg-white">
                                <img
                                    src={logo}
                                    alt="Active Motion Logo"
                                    className="w-12 h-12 object-contain"
                                />
                            </div>
                            <div>
                                <h1 className="text-2xl font-display font-bold text-gray-900">
                                    ACTIVE MOTION
                                </h1>
                                <p className="text-xs text-gray-600 tracking-wider font-medium">
                                    Movement is Medicine ♯
                                </p>
                            </div>
                        </div>

                        {/* Desktop Menu */}
                        <div className="hidden md:flex items-center space-x-8 animate-slide-in-right">
                            <a
                                href="#services"
                                className="text-gray-700 hover:text-blue-600 font-medium transition-colors"
                            >
                                Services
                            </a>
                            <a
                                href="#doctors"
                                className="text-gray-700 hover:text-blue-600 font-medium transition-colors"
                            >
                                Our Doctors
                            </a>
                            <a
                                href="#why-us"
                                className="text-gray-700 hover:text-blue-600 font-medium transition-colors"
                            >
                                Why Us
                            </a>
                            <a
                                href="#testimonials"
                                className="text-gray-700 hover:text-blue-600 font-medium transition-colors"
                            >
                                Testimonials
                            </a>
                            <a
                                href="#contact"
                                className="text-gray-700 hover:text-blue-600 font-medium transition-colors"
                            >
                                Contact
                            </a>
                            <a
                                href={`https://wa.me/${whatsappNumber}?text=Hi!%20I%20want%20to%20book%20a%20consultation`}
                            >
                                <button className="bg-gradient-to-r from-blue-500 to-green-500 text-white px-6 py-3 rounded-full font-semibold hover:shadow-xl transition-all duration-300 hover:scale-105">
                                    Book Appointment
                                </button>
                            </a>
                        </div>

                        {/* Mobile Menu Button */}
                        <button
                            onClick={() => setIsMenuOpen(!isMenuOpen)}
                            className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
                        </button>
                    </div>
                </div>

                {/* Mobile Menu */}
                {isMenuOpen && (
                    <div className="md:hidden glass-effect border-t border-gray-200 animate-fade-in-up">
                        <div className="px-4 py-6 space-y-4">
                            <a
                                href="#services"
                                className="block text-gray-700 hover:text-blue-600 font-medium transition-colors"
                            >
                                Services
                            </a>
                            <a
                                href="#doctors"
                                className="block text-gray-700 hover:text-blue-600 font-medium transition-colors"
                            >
                                Our Doctors
                            </a>
                            <a
                                href="#why-us"
                                className="block text-gray-700 hover:text-blue-600 font-medium transition-colors"
                            >
                                Why Us
                            </a>
                            <a
                                href="#testimonials"
                                className="block text-gray-700 hover:text-blue-600 font-medium transition-colors"
                            >
                                Testimonials
                            </a>
                            <a
                                href="#contact"
                                className="block text-gray-700 hover:text-blue-600 font-medium transition-colors"
                            >
                                Contact
                            </a>
                            <a
                                href={`https://wa.me/${whatsappNumber}?text=Hi!%20I%20want%20to%20book%20a%20consultation`}
                            >
                                <button className="w-full bg-gradient-to-r from-blue-500 to-green-500 text-white px-6 py-3 rounded-full font-semibold">
                                    Book Appointment
                                </button>
                            </a>
                        </div>
                    </div>
                )}
            </nav>

            {/* Hero Section */}
            <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
                <div className="absolute inset-0 mesh-gradient opacity-50"></div>

                {/* Floating Blobs */}
                <div
                    className="absolute top-20 left-10 w-72 h-72 bg-blue-200 opacity-30 blob"
                    style={{ animationDelay: "0s" }}
                ></div>
                <div
                    className="absolute bottom-20 right-10 w-96 h-96 bg-green-200 opacity-30 blob"
                    style={{ animationDelay: "2s" }}
                ></div>
                <div
                    className="absolute top-1/2 left-1/2 w-64 h-64 bg-yellow-200 opacity-20 blob"
                    style={{ animationDelay: "4s" }}
                ></div>

                <div className="max-w-7xl mx-auto relative z-10">
                    <div className="grid md:grid-cols-2 gap-12 items-center">
                        <div className="space-y-8">
                            <div
                                className="animate-slide-in-left opacity-0"
                                style={{ animationDelay: "0.2s" }}
                            >
                                <span className="inline-block bg-gradient-to-r from-blue-500 to-green-500 text-white px-4 py-2 rounded-full text-sm font-semibold mb-4">
                                    ✨ Movement is Medicine
                                </span>
                                <h2 className="text-5xl md:text-7xl font-display font-black text-gray-900 leading-tight mb-6">
                                    Heal Naturally,
                                    <br />
                                    <span className="gradient-text">
                                        Move Better
                                    </span>
                                </h2>
                                <p className="text-xl text-gray-600 leading-relaxed font-light">
                                    Expert physiotherapy care by{" "}
                                    <span className="font-bold text-blue-600">
                                        Dr. Mosam Patel
                                    </span>{" "}
                                    and{" "}
                                    <span className="font-bold text-green-600">
                                        Dr. Aayushi Patel
                                    </span>
                                    . Treat the cause, not just the pain.
                                </p>
                            </div>

                            <div className="flex flex-col sm:flex-row gap-4 animate-slide-in-left opacity-0 delay-400">
                                <a
                                    href={`https://wa.me/${whatsappNumber}?text=Hi!%20I%20want%20to%20book%20a%20consultation`}
                                >
                                    <button className="group bg-gradient-to-r from-blue-500 via-green-500 to-yellow-500 text-white px-8 py-4 rounded-full font-bold text-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 flex items-center justify-center space-x-2">
                                        <Calendar size={24} />
                                        <span>Book Consultation</span>
                                        <ArrowRight
                                            size={20}
                                            className="group-hover:translate-x-1 transition-transform"
                                        />
                                    </button>
                                </a>
                                {/* <button className="bg-white text-gray-900 px-8 py-4 rounded-full font-bold text-lg border-2 border-gray-900 hover:bg-gray-900 hover:text-white transition-all duration-300 hover:scale-105">
                                    Learn More
                                </button> */}
                            </div>

                            <div className="grid grid-cols-3 gap-4 pt-8 animate-slide-in-left opacity-0 delay-600">
                                <div className="text-center">
                                    <div className="text-3xl font-black gradient-text">
                                        500+
                                    </div>
                                    <div className="text-sm text-gray-600 font-medium">
                                        Happy Patients
                                    </div>
                                </div>
                                {/* <div className="text-center">
                  <div className="text-3xl font-black gradient-text">15+</div>
                  <div className="text-sm text-gray-600 font-medium">Years Combined Experience</div>
                </div> */}
                                <div className="text-center">
                                    <div className="text-3xl font-black gradient-text">
                                        95%
                                    </div>
                                    <div className="text-sm text-gray-600 font-medium">
                                        Success Rate
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="relative animate-slide-in-right opacity-0 delay-300">
                            <div className="relative z-10">
                                <div className="bg-gradient-to-br from-blue-500 via-green-500 to-yellow-500 p-1 rounded-3xl shadow-2xl">
                                    <div className="bg-white p-8 rounded-3xl">
                                        <div className="space-y-6">
                                            <div className="flex items-start space-x-4 p-4 bg-green-50 rounded-2xl border-l-4 border-green-500">
                                                <CheckCircle
                                                    className="text-green-500 mt-1 flex-shrink-0"
                                                    size={28}
                                                />
                                                <div>
                                                    <h3 className="font-bold text-gray-900 text-lg">
                                                        Evidence-Based Treatment
                                                    </h3>
                                                    <p className="text-gray-600">
                                                        Science-backed care for
                                                        lasting results
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex items-start space-x-4 p-4 bg-blue-50 rounded-2xl border-l-4 border-blue-500">
                                                <CheckCircle
                                                    className="text-blue-500 mt-1 flex-shrink-0"
                                                    size={28}
                                                />
                                                <div>
                                                    <h3 className="font-bold text-gray-900 text-lg">
                                                        Treat the Root Cause
                                                    </h3>
                                                    <p className="text-gray-600">
                                                        Not just symptoms—real
                                                        healing
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex items-start space-x-4 p-4 bg-yellow-50 rounded-2xl border-l-4 border-yellow-500">
                                                <CheckCircle
                                                    className="text-yellow-600 mt-1 flex-shrink-0"
                                                    size={28}
                                                />
                                                <div>
                                                    <h3 className="font-bold text-gray-900 text-lg">
                                                        Natural Pain Relief
                                                    </h3>
                                                    <p className="text-gray-600">
                                                        Avoid dependency on
                                                        painkillers
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-gradient-to-br from-blue-300 to-purple-300 rounded-full opacity-50 blur-xl animate-pulse"></div>
                        </div>
                    </div>
                </div>
            </section>

            {/* The Correct Formula Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-900 to-gray-800 text-white relative overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500 rounded-full filter blur-3xl"></div>
                    <div className="absolute bottom-0 right-0 w-96 h-96 bg-green-500 rounded-full filter blur-3xl"></div>
                </div>

                <div className="max-w-7xl mx-auto relative z-10">
                    <div className="text-center mb-16">
                        <div className="inline-block bg-red-500 text-white px-6 py-2 rounded-full text-lg font-bold mb-6 animate-pulse">
                            ⚠️ STRETCHING IS NOT ENOUGH!!!
                        </div>
                        <h2 className="text-5xl font-display font-black mb-6">
                            The{" "}
                            <span className="text-green-400">
                                Correct Formula
                            </span>
                        </h2>
                        <p className="text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
                            Stretching feels good, but if your muscles are
                            weak... pain returns again.
                            <br />
                            <span className="text-green-400 font-bold">
                                That's why we combine all three.
                            </span>
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8 mt-16">
                        <div className="bg-white/10 backdrop-blur-lg p-8 rounded-3xl border border-white/20 hover:bg-white/15 transition-all duration-300 animate-fade-in-up opacity-0 delay-200">
                            <div className="w-16 h-16 bg-gradient-to-br from-green-400 to-green-600 rounded-2xl flex items-center justify-center text-3xl mb-6">
                                ✓
                            </div>
                            <h3 className="text-2xl font-bold mb-4">
                                1. Stretch
                            </h3>
                            <p className="text-gray-300 leading-relaxed">
                                Release tight muscles and improve flexibility to
                                reduce immediate discomfort and restore range of
                                motion.
                            </p>
                        </div>

                        <div className="bg-white/10 backdrop-blur-lg p-8 rounded-3xl border border-white/20 hover:bg-white/15 transition-all duration-300 animate-fade-in-up opacity-0 delay-400">
                            <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-blue-600 rounded-2xl flex items-center justify-center text-3xl mb-6">
                                ✓
                            </div>
                            <h3 className="text-2xl font-bold mb-4">
                                2. Strengthen
                            </h3>
                            <p className="text-gray-300 leading-relaxed">
                                Build weak supporting muscles to address the
                                root cause and prevent pain from returning.
                            </p>
                        </div>

                        <div className="bg-white/10 backdrop-blur-lg p-8 rounded-3xl border border-white/20 hover:bg-white/15 transition-all duration-300 animate-fade-in-up opacity-0 delay-600">
                            <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-2xl flex items-center justify-center text-3xl mb-6">
                                ✓
                            </div>
                            <h3 className="text-2xl font-bold mb-4">
                                3. Posture Correction
                            </h3>
                            <p className="text-gray-300 leading-relaxed">
                                Optimize body mechanics and movement patterns to
                                ensure long-term wellness and injury prevention.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Comprehensive Services Section */}
            <section
                id="services"
                className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white to-gray-50"
            >
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16 animate-fade-in-up opacity-0">
                        <h2 className="text-5xl font-display font-black text-gray-900 mb-4">
                            Comprehensive{" "}
                            <span className="gradient-text">Care</span> for All
                            Your Needs
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            From everyday pain to specialized conditions, we
                            provide expert treatment for lasting wellness
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {services.map((service, index) => (
                            <div
                                key={index}
                                className="service-card bg-white rounded-3xl p-6 shadow-xl border-2 border-gray-100 hover:border-gray-200 animate-fade-in-up opacity-0 cursor-pointer"
                                style={{
                                    animationDelay: `${0.1 + index * 0.1}s`,
                                }}
                                onClick={() =>
                                    setActiveService(
                                        activeService === index ? null : index,
                                    )
                                }
                            >
                                <div
                                    className="w-16 h-16 rounded-2xl flex items-center justify-center text-3xl mb-4 shadow-lg"
                                    style={{ backgroundColor: service.color }}
                                >
                                    {service.icon}
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-2">
                                    {service.title}
                                </h3>
                                <p className="text-gray-600 mb-4 text-sm leading-relaxed">
                                    {service.description}
                                </p>
                                {activeService === index && (
                                    <div className="mt-4 pt-4 border-t border-gray-200 animate-fade-in-up">
                                        <p className="text-sm font-semibold text-gray-700 mb-2">
                                            We treat:
                                        </p>
                                        <ul className="space-y-1">
                                            {service.conditions.map(
                                                (condition, idx) => (
                                                    <li
                                                        key={idx}
                                                        className="text-sm text-gray-600 flex items-center"
                                                    >
                                                        <span className="text-green-500 mr-2">
                                                            ✓
                                                        </span>
                                                        {condition}
                                                    </li>
                                                ),
                                            )}
                                        </ul>
                                    </div>
                                )}
                                <button className="w-full bg-gray-900 text-white py-3 rounded-full font-semibold hover:bg-gray-800 transition-colors flex items-center justify-center space-x-2 mt-4">
                                    <span>
                                        {activeService === index
                                            ? "Less Info"
                                            : "Learn More"}
                                    </span>
                                    <ArrowRight size={18} />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Meet Our Doctors Section */}
            <section
                id="doctors"
                className="py-20 px-4 sm:px-6 lg:px-8 bg-white"
            >
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-5xl font-display font-black text-gray-900 mb-4">
                            Meet Your{" "}
                            <span className="gradient-text">Expert</span>{" "}
                            Physiotherapists
                        </h2>
                        {/* <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Combined experience of 15+ years, dedicated to your recovery and wellness
            </p> */}
                    </div>

                    <div className="grid md:grid-cols-2 gap-12">
                        {doctors.map((doctor, index) => (
                            <div
                                key={index}
                                className="animate-fade-in-up opacity-0"
                                style={{ animationDelay: `${index * 0.2}s` }}
                            >
                                <div className="bg-gradient-to-br from-gray-50 to-white rounded-3xl p-8 shadow-xl border-2 border-gray-100 hover:shadow-2xl transition-all duration-300">
                                    <div className="flex items-start space-x-6 mb-6">
                                        <div
                                            className="w-24 h-24 rounded-2xl flex items-center justify-center text-5xl shadow-lg flex-shrink-0"
                                            style={{
                                                backgroundColor:
                                                    doctor.color + "20",
                                            }}
                                        >
                                            {doctor.image}
                                        </div>
                                        <div>
                                            <h3 className="text-2xl font-bold text-gray-900 mb-1">
                                                {doctor.name}
                                            </h3>
                                            <p
                                                className="text-lg font-semibold mb-2"
                                                style={{ color: doctor.color }}
                                            >
                                                {doctor.designation}
                                            </p>
                                            {/* <div className="flex items-center space-x-2 text-gray-600">
                                                <Award size={18} />
                                                <span className="text-sm font-medium">
                                                    {doctor.experience}{" "}
                                                    Experience
                                                </span>
                                            </div> */}
                                        </div>
                                    </div>

                                    <div className="mb-6 p-6 bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl relative overflow-hidden">
                                        <div className="absolute top-0 left-0 text-6xl text-white opacity-10">
                                            "
                                        </div>
                                        <p className="text-white italic leading-relaxed relative z-10 pl-8">
                                            {doctor.quote}
                                        </p>
                                        <div className="absolute bottom-0 right-0 text-6xl text-white opacity-10 transform rotate-180">
                                            "
                                        </div>
                                    </div>

                                    <div>
                                        <p className="text-sm font-semibold text-gray-700 mb-3">
                                            Areas of Expertise:
                                        </p>
                                        <div className="flex flex-wrap gap-2">
                                            {doctor.expertise.map(
                                                (skill, idx) => (
                                                    <span
                                                        key={idx}
                                                        className="px-4 py-2 rounded-full text-sm font-medium text-white"
                                                        style={{
                                                            backgroundColor:
                                                                doctor.color,
                                                        }}
                                                    >
                                                        {skill}
                                                    </span>
                                                ),
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Why Choose Us */}
            <section
                id="why-us"
                className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 via-white to-green-50"
            >
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-5xl font-display font-black text-gray-900 mb-4">
                            Why{" "}
                            <span className="gradient-text">
                                Active Motion?
                            </span>
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            We don't just treat pain—we transform lives through
                            movement
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        <div className="bg-white rounded-3xl p-8 shadow-xl border-2 border-gray-100 hover:shadow-2xl transition-all duration-300 animate-fade-in-up opacity-0">
                            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mb-6">
                                <Target className="text-white" size={32} />
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-4">
                                Root Cause Treatment
                            </h3>
                            <p className="text-gray-600 leading-relaxed">
                                We identify and address the underlying cause,
                                not just mask symptoms with temporary relief.
                            </p>
                        </div>

                        <div className="bg-white rounded-3xl p-8 shadow-xl border-2 border-gray-100 hover:shadow-2xl transition-all duration-300 animate-fade-in-up opacity-0 delay-200">
                            <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center mb-6">
                                <Heart className="text-white" size={32} />
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-4">
                                Patient-Centered Care
                            </h3>
                            <p className="text-gray-600 leading-relaxed">
                                Every treatment plan is personalized to your
                                unique condition, lifestyle, and goals.
                            </p>
                        </div>

                        <div className="bg-white rounded-3xl p-8 shadow-xl border-2 border-gray-100 hover:shadow-2xl transition-all duration-300 animate-fade-in-up opacity-0 delay-400">
                            <div className="w-16 h-16 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-2xl flex items-center justify-center mb-6">
                                <Shield className="text-white" size={32} />
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-4">
                                Natural Healing
                            </h3>
                            <p className="text-gray-600 leading-relaxed">
                                Drug-free, surgery-free treatment to help your
                                body heal naturally and sustainably.
                            </p>
                        </div>

                        <div className="bg-white rounded-3xl p-8 shadow-xl border-2 border-gray-100 hover:shadow-2xl transition-all duration-300 animate-fade-in-up opacity-0 delay-600">
                            <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mb-6">
                                <Users className="text-white" size={32} />
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-4">
                                Expert Team
                            </h3>
                            <p className="text-gray-600 leading-relaxed">
                                15+ years combined experience with continuous
                                education in latest physiotherapy techniques.
                            </p>
                        </div>

                        <div className="bg-white rounded-3xl p-8 shadow-xl border-2 border-gray-100 hover:shadow-2xl transition-all duration-300 animate-fade-in-up opacity-0 delay-700">
                            <div className="w-16 h-16 bg-gradient-to-br from-pink-500 to-pink-600 rounded-2xl flex items-center justify-center mb-6">
                                <Zap className="text-white" size={32} />
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-4">
                                Proven Results
                            </h3>
                            <p className="text-gray-600 leading-relaxed">
                                95% success rate with 500+ satisfied patients
                                who've regained their active lifestyle.
                            </p>
                        </div>

                        <div className="bg-white rounded-3xl p-8 shadow-xl border-2 border-gray-100 hover:shadow-2xl transition-all duration-300 animate-fade-in-up opacity-0 delay-800">
                            <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center mb-6">
                                <Award className="text-white" size={32} />
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-4">
                                Holistic Approach
                            </h3>
                            <p className="text-gray-600 leading-relaxed">
                                Combining manual therapy, exercise, education,
                                and lifestyle modifications for complete
                                healing.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Testimonials Section */}
            <section
                id="testimonials"
                className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-900 to-gray-800 text-white"
            >
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-5xl font-display font-black mb-4">
                            Patient{" "}
                            <span className="text-green-400">
                                Success Stories
                            </span>
                        </h2>
                        <p className="text-xl text-gray-300">
                            Real results from real people
                        </p>
                    </div>

                    <div className="relative">
                        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-12 border border-white/20">
                            <div className="flex items-center justify-center mb-4">
                                {[
                                    ...Array(
                                        testimonials[activeTestimonial].rating,
                                    ),
                                ].map((_, i) => (
                                    <Star
                                        key={i}
                                        className="text-yellow-400 fill-current"
                                        size={24}
                                    />
                                ))}
                            </div>

                            <p className="text-2xl text-white leading-relaxed mb-8 text-center italic">
                                "{testimonials[activeTestimonial].text}"
                            </p>

                            <div className="text-center">
                                <p className="text-xl font-bold text-white mb-1">
                                    {testimonials[activeTestimonial].name}
                                </p>
                                <p className="text-gray-400 mb-2">
                                    {testimonials[activeTestimonial].issue}
                                </p>
                                <div className="inline-block bg-green-500 text-white px-4 py-2 rounded-full text-sm font-semibold">
                                    ✓ {testimonials[activeTestimonial].result}
                                </div>
                            </div>
                        </div>

                        <button
                            onClick={prevTestimonial}
                            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-12 w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-lg hover:bg-gray-100 transition-colors"
                        >
                            <ChevronLeft className="text-gray-900" size={24} />
                        </button>

                        <button
                            onClick={nextTestimonial}
                            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-12 w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-lg hover:bg-gray-100 transition-colors"
                        >
                            <ChevronRight className="text-gray-900" size={24} />
                        </button>
                    </div>

                    <div className="flex justify-center mt-8 space-x-2">
                        {testimonials.map((_, index) => (
                            <button
                                key={index}
                                onClick={() => setActiveTestimonial(index)}
                                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                                    index === activeTestimonial
                                        ? "bg-green-400 w-8"
                                        : "bg-white/30"
                                }`}
                            />
                        ))}
                    </div>
                </div>
            </section>

            {/* Contact Section */}
            <section
                id="contact"
                className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 to-green-50"
            >
                <div className="max-w-7xl mx-auto">
                    <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
                        <div className="grid md:grid-cols-2">
                            <div className="bg-gradient-to-br from-gray-900 to-gray-800 p-12 text-white">
                                <h2 className="text-4xl font-display font-black mb-6">
                                    Ready to Move Better?
                                </h2>
                                <p className="text-gray-300 mb-8 text-lg">
                                    Book your consultation today and start your
                                    journey to pain-free living!
                                </p>

                                <div className="space-y-6">
                                    {/* <div className="flex items-start space-x-4">
                                        <MapPin
                                            className="text-green-400 flex-shrink-0 mt-1"
                                            size={24}
                                        />
                                        <div>
                                            <h3 className="font-bold mb-1">
                                                Location
                                            </h3>
                                            <p className="text-gray-300">
                                                Active Motion Physiotherapy
                                                Clinic
                                                <br />
                                                Surat, Gujarat, India
                                            </p>
                                        </div>
                                    </div> */}
                                    <div className="flex items-start space-x-4">
                                        <MapPin
                                            className="text-green-400 flex-shrink-0 mt-1"
                                            size={24}
                                        />
                                        <div>
                                            <h3 className="font-bold mb-2">
                                                Location
                                            </h3>
                                            <a
                                                href="https://maps.google.com/?q=Active+Motion+Physiotherapy+Clinic+Surat+Gujarat"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                <p className="text-gray-300 mb-3">
                                                    {clinicAddress.line1}
                                                    <br />
                                                    {clinicAddress.line2}
                                                    <br />
                                                    <b>
                                                        {clinicAddress.landmark}
                                                    </b>
                                                    <br />
                                                    {clinicAddress.area},{" "}
                                                    {clinicAddress.city},{" "}
                                                    {clinicAddress.zip}
                                                </p>
                                            </a>
                                            <a
                                                href="https://maps.google.com/?q=Active+Motion+Physiotherapy+Clinic+Surat+Gujarat"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center space-x-2 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 hover:shadow-xl hover:scale-105"
                                            >
                                                <svg
                                                    className="w-5 h-5"
                                                    viewBox="0 0 24 24"
                                                    fill="currentColor"
                                                >
                                                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                                                </svg>
                                                <span>View on Google Maps</span>
                                            </a>
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-4">
                                        <Phone
                                            className="text-blue-400 flex-shrink-0 mt-1"
                                            size={24}
                                        />
                                        <div>
                                            <h3 className="font-bold mb-1">
                                                Phone
                                            </h3>

                                            <a
                                                href={`tel:${whatsappNumber.replace(/\s+/g, "")}`}
                                                className="text-gray-300 hover:text-blue-400 transition"
                                            >
                                                {contactNumber}
                                            </a>
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-4">
                                        <Mail
                                            className="text-yellow-400 flex-shrink-0 mt-1"
                                            size={24}
                                        />
                                        <div>
                                            <h3 className="font-bold mb-1">
                                                Email
                                            </h3>

                                            <a
                                                href={`mailto:${emailAddress}`}
                                                className="text-gray-300 hover:text-yellow-400 transition"
                                            >
                                                {emailAddress}
                                            </a>
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-4">
                                        <Clock
                                            className="text-purple-400 flex-shrink-0 mt-1"
                                            size={24}
                                        />
                                        <div>
                                            <h3 className="font-bold mb-1">
                                                Hours
                                            </h3>
                                            <div className="text-gray-300 space-y-2">
                                                <div className="flex gap-2">
                                                    <span className="font-medium text-white min-w-[90px]">
                                                        Mon–Sat:
                                                    </span>
                                                    <span>
                                                        7:00 AM – 12:00 PM
                                                    </span>
                                                </div>
                                                <div className="flex gap-2">
                                                    <span className="font-medium text-white min-w-[90px]"></span>
                                                    <span>
                                                        4:00 PM – 9:00 PM
                                                    </span>
                                                </div>

                                                <div className="flex gap-2">
                                                    <span className="font-medium text-white min-w-[90px]">
                                                        Sunday:
                                                    </span>
                                                    <span>By Appointment</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-8 pt-8 border-t border-white/20">
                                    <p className="text-sm text-gray-400 mb-4">
                                        Follow us on Instagram
                                    </p>
                                    <a
                                        href="https://www.instagram.com/activemotion_physiotherapy/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center space-x-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-3 rounded-full font-semibold hover:shadow-xl transition-all duration-300 hover:scale-105"
                                    >
                                        <span>📷</span>
                                        <span>@activemotion_physiotherapy</span>
                                    </a>
                                </div>
                            </div>

                            <div className="p-12">
                                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                                    Send Us a Message
                                </h3>
                                <form
                                    onSubmit={handleWhatsAppSubmit}
                                    className="space-y-4"
                                >
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            Name *
                                        </label>
                                        <input
                                            type="text"
                                            required
                                            className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-blue-500 focus:outline-none transition-colors"
                                            placeholder="Your full name"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            Phone *
                                        </label>
                                        <input
                                            type="tel"
                                            required
                                            className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-blue-500 focus:outline-none transition-colors"
                                            placeholder="+91 XXXXX XXXXX"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            Email
                                        </label>
                                        <input
                                            type="email"
                                            className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-blue-500 focus:outline-none transition-colors"
                                            placeholder="your@email.com"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            What brings you here? *
                                        </label>
                                        <select
                                            required
                                            className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-blue-500 focus:outline-none transition-colors"
                                            name="concern"
                                            value={formData.concern}
                                            onChange={handleInputChange}
                                        >
                                            <option value="">
                                                Select your concern
                                            </option>
                                            <option value="back-pain">
                                                Back Pain
                                            </option>
                                            <option value="neck-pain">
                                                Neck Pain
                                            </option>
                                            <option value="knee-pain">
                                                Knee Pain
                                            </option>
                                            <option value="womens-health">
                                                Women's Health
                                            </option>
                                            <option value="plantar-fasciitis">
                                                Plantar Fasciitis
                                            </option>
                                            <option value="sports-injury">
                                                Sports Injury
                                            </option>
                                            <option value="arthritis">
                                                Arthritis
                                            </option>
                                            <option value="other">Other</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            Additional Details
                                        </label>
                                        <textarea
                                            name="details"
                                            rows="4"
                                            className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-blue-500 focus:outline-none transition-colors resize-none"
                                            placeholder="Tell us more about your condition..."
                                            value={formData.details}
                                            onChange={handleInputChange}
                                        ></textarea>
                                    </div>
                                    <button
                                        type="submit"
                                        className="w-full bg-gradient-to-r from-blue-500 via-green-500 to-yellow-500 text-white py-4 rounded-xl font-bold text-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                                    >
                                        Book Appointment
                                    </button>
                                    <p className="text-xs text-gray-500 text-center">
                                        We'll get back to you within 24 hours
                                    </p>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-gray-900 text-white py-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-7xl mx-auto">
                    <div className="grid md:grid-cols-4 gap-8 mb-8">
                        <div>
                            <div className="flex items-center space-x-2 mb-4">
                                <div className="w-14 h-14 rounded-full flex items-center justify-center shadow-lg bg-white">
                                    <img
                                        src={logo}
                                        alt="Active Motion Logo"
                                        className="w-12 h-12 object-contain"
                                    />
                                </div>
                                <div>
                                    <h3 className="font-display font-bold text-lg">
                                        ACTIVE MOTION
                                    </h3>
                                    <p className="text-xs text-gray-400">
                                        Movement is Medicine ♯
                                    </p>
                                </div>
                            </div>
                            <p className="text-gray-400 text-sm">
                                Expert physiotherapy care in Surat, Gujarat.
                                Helping you move better and live pain-free.
                            </p>
                        </div>
                        <div>
                            <h4 className="font-bold mb-4">Quick Links</h4>
                            <ul className="space-y-2 text-gray-400 text-sm">
                                <li>
                                    <a
                                        href="#services"
                                        className="hover:text-white transition-colors"
                                    >
                                        Our Services
                                    </a>
                                </li>
                                <li>
                                    <a
                                        href="#doctors"
                                        className="hover:text-white transition-colors"
                                    >
                                        Our Doctors
                                    </a>
                                </li>
                                <li>
                                    <a
                                        href="#why-us"
                                        className="hover:text-white transition-colors"
                                    >
                                        Why Choose Us
                                    </a>
                                </li>
                                <li>
                                    <a
                                        href="#testimonials"
                                        className="hover:text-white transition-colors"
                                    >
                                        Testimonials
                                    </a>
                                </li>
                                <li>
                                    <a
                                        href="#contact"
                                        className="hover:text-white transition-colors"
                                    >
                                        Contact
                                    </a>
                                </li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-bold mb-4">Services</h4>
                            <ul className="space-y-2 text-gray-400 text-sm">
                                <li>Back Pain Relief</li>
                                <li>Knee Pain Management</li>
                                <li>Women's Health Physio</li>
                                <li>Neck & Spine Care</li>
                                <li>Plantar Fasciitis Treatment</li>
                                <li>Sports Rehabilitation</li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-bold mb-4">Connect With Us</h4>
                            <div className="flex space-x-4 mb-4">
                                <a
                                    href="https://www.instagram.com/activemotion_physiotherapy/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center hover:scale-110 transition-transform"
                                >
                                    <span>📷</span>
                                </a>
                                <a
                                    href="https://www.instagram.com/activemotion_physiotherapy/"
                                    className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-blue-600 transition-colors"
                                >
                                    <span>f</span>
                                </a>
                                <a
                                    href="https://www.instagram.com/activemotion_physiotherapy/"
                                    className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-green-600 transition-colors"
                                >
                                    <span>📱</span>
                                </a>
                            </div>
                            <p className="text-gray-400 text-sm">
                                Stay updated with health tips and exercises on
                                our Instagram!
                            </p>
                        </div>
                    </div>
                    <div className="border-t border-gray-800 pt-8 text-center text-gray-400 text-sm">
                        <p>
                            © 2026 Active Motion Physiotherapy. All rights
                            reserved.
                        </p>
                        <p className="mt-2">
                            Dr. Mosam Patel (PT) | Dr. Aayushi Patel (PT)
                        </p>
                    </div>
                </div>
            </footer>

            {/* // Add before the closing </div> of your main component: */}

            <a
                href={`https://wa.me/${whatsappNumber}?text=Hi!%20I%20have%20a%20question%20about%20physiotherapy`}
                target="_blank"
                rel="noopener noreferrer"
                className="fixed bottom-6 right-6 w-16 h-16 bg-green-500 rounded-full flex items-center justify-center shadow-2xl hover:bg-green-600 transition-all duration-300 hover:scale-110 z-50"
                aria-label="Contact us on WhatsApp"
            >
                <span className="text-3xl">💬</span>
            </a>
        </div>
    );
}
